/**
 * Site Search Widget - Embeddable Bundle Entry Point
 *
 * Adds MinistryPlatform's events, Life Groups and serve opportunities to the
 * WordPress search results page on woodsidebible.org. WordPress only knows
 * its own pages and posts; this reads the page's `?s=` query and puts three
 * more sections above the theme's result cards, each card the same one its
 * finder shows.
 *
 * Loaded site-wide from the theme's "Custom JS Code (Head)" box:
 *   <script src="https://widgets.woodsidebible.org/site-search/widget.js" defer></script>
 *
 * It does nothing unless the page is a search results page (a non-empty
 * `?s=`) and the theme's results container (`#woodside-search-results`) is
 * on it; the widget mounts into `#site-search-root`, creating it just above
 * the results when the page has not placed one. Pages 2+ of the WordPress
 * results are left alone. Renders inside a shadow root for style isolation
 * from the theme (see shell.tsx).
 *
 * The theme's title band ("Results For "bible"", "250 results found") gets
 * a search box and the campus chips under its count line, in a second
 * shadow root (`#site-search-hero`), and the count line is rewritten to one
 * number for the whole page: to a visitor it is one search, not WordPress
 * plus MinistryPlatform.
 *
 * It also takes down the theme's "Sorry, no results were found" block when
 * we found something, because WordPress decides that line on the server and
 * has no way to know. See setThemeApology.
 *
 * Search & Filter Pro's ajax mode is not on for this form today; if it is
 * turned on, the widget follows its `sf:ajaxfinish` event and re-reads the
 * query.
 */

import React from 'react';
import { setSeeJesusAssetBase } from '@church/nextjs-ui/website';
import { SiteSearch, type SiteSearchCounts } from '../src/components/SiteSearch';
import { attr } from './params';
import { ShadowWidget } from './shell';

interface WidgetConfig {
  apiBaseUrl?: string;
  /** Where the See Jesus assets live (default: the group finder's folder on widgets-host) */
  assetBaseUrl?: string;
  /** Mount here instead of creating a container above the results */
  containerId?: string;
  /** The theme's results container the widget mounts above */
  resultsId?: string;
  /** The theme's count line in the title band; the search box goes under it */
  countSelector?: string;
  /** Put the search box here instead of under the count line */
  heroId?: string;
  /** Read the query from this URL parameter (WordPress: `s`) */
  queryParam?: string;
  eventDetailsUrl?: string;
  groupDetailsUrl?: string;
  serveDetailsUrl?: string;
  pageSize?: number;
}

declare global {
  interface Window {
    SITE_SEARCH_WIDGET_CONFIG?: WidgetConfig;
    SiteSearchWidget?: { query: string | null; widget: ShadowWidget | null; refresh: () => void };
    jQuery?: (target: Document) => { on: (event: string, handler: () => void) => void };
  }
}

const DEFAULT_CONTAINER_ID = 'site-search-root';
const DEFAULT_RESULTS_ID = 'woodside-search-results';
const DEFAULT_HERO_ID = 'site-search-hero';
const DEFAULT_COUNT_SELECTOR = '.result-num';
const DEFAULT_ASSET_BASE = 'https://widgets.woodsidebible.org/group-finder';

function currentQuery(param: string): string | null {
  try {
    const q = new URLSearchParams(window.location.search).get(param)?.trim();
    return q ? q : null;
  } catch {
    return null;
  }
}

/** WordPress paginates its results at /page/N/; the widget shows on the first page only. */
function isFirstPage(): boolean {
  return !/\/page\/\d+\/?$/.test(window.location.pathname);
}

/**
 * Where to mount: an explicit container if the page has one, otherwise a
 * new one just above the theme's results grid.
 */
function findContainer(config: WidgetConfig): HTMLElement | null {
  const id = config.containerId || DEFAULT_CONTAINER_ID;
  const existing = document.getElementById(id);
  if (existing) return existing;
  const results = document.getElementById(config.resultsId || DEFAULT_RESULTS_ID);
  if (!results || !results.parentElement) return null;
  const el = document.createElement('div');
  el.id = id;
  el.style.margin = '0 0 48px';
  results.parentElement.insertBefore(el, results);
  return el;
}

/**
 * The search box's home: a spot in the theme's title band, right under its
 * count line, so the page reads title, box, count. Salient sizes that band
 * to a fixed height and centres the text block in it, so the band is let
 * grow to fit. Null when the page has no count line (the box then renders
 * above the results instead).
 */
function findHero(config: WidgetConfig): { el: HTMLElement; themeCounted: boolean } | null {
  const id = config.heroId || DEFAULT_HERO_ID;
  const existing = document.getElementById(id);
  if (existing) return { el: existing, themeCounted: existing.dataset.themeCounted === '1' };
  // When WordPress found nothing it prints the title but no count line, so
  // the box goes after the title instead and the widget writes the count.
  const count = document.querySelector<HTMLElement>(config.countSelector || DEFAULT_COUNT_SELECTOR);
  const title = document.querySelector<HTMLElement>('#page-header-bg .inner-wrap h1');
  const anchor = count ?? title;
  if (!anchor || !anchor.parentElement) return null;
  const el = document.createElement('div');
  el.id = id;
  el.style.margin = count ? '28px 0 0' : '16px 0 0';
  el.dataset.themeCounted = count ? '1' : '0';
  anchor.insertAdjacentElement('afterend', el);
  openTitleBand(anchor);
  return { el, themeCounted: Boolean(count) };
}

/** Let Salient's fixed-height title band grow around the search box. */
function openTitleBand(inside: HTMLElement) {
  const band = inside.closest<HTMLElement>('#page-header-bg');
  if (!band) return;
  band.style.setProperty('height', 'auto', 'important');
  band.style.setProperty('min-height', '0', 'important');
  band.style.setProperty('padding', '56px 0', 'important');
  for (const el of band.querySelectorAll<HTMLElement>('.container, .row')) {
    el.style.setProperty('height', 'auto', 'important');
  }
  const col = inside.closest<HTMLElement>('.col');
  if (col) {
    col.style.setProperty('position', 'static', 'important');
    col.style.setProperty('top', 'auto', 'important');
    col.style.setProperty('transform', 'none', 'important');
  }
}

/**
 * The theme's "248 results found" line only counts WordPress. To a visitor
 * this is one search, so the line shows one number: "308 results found".
 * On a failure the line goes back to the theme's own count.
 */
function annotateCount(config: WidgetConfig, counts: SiteSearchCounts | null) {
  const el = document.querySelector<HTMLElement>(config.countSelector || DEFAULT_COUNT_SELECTOR);
  if (!el || el.closest(`#${config.heroId || DEFAULT_HERO_ID}`)) return;
  const base = el.dataset.siteSearchBase ?? el.textContent ?? '';
  el.dataset.siteSearchBase = base;
  const ours = counts ? counts.events + counts.groups + counts.opportunities : 0;
  if (!counts || ours === 0) {
    el.textContent = base;
    return;
  }
  const m = base.match(/\d[\d,]*/);
  if (m) {
    const theirs = Number(m[0].replace(/,/g, '')) || 0;
    const total = theirs + ours;
    el.textContent = base.replace(m[0], total.toLocaleString('en-US'));
    // "1 result found" would read wrong once ours are added.
    if (total !== 1) el.textContent = el.textContent.replace(/\bresult\b/, 'results');
    return;
  }
  el.textContent = `${ours.toLocaleString('en-US')} ${ours === 1 ? 'result' : 'results'} found`;
}

/**
 * WordPress apologising for something it cannot see.
 *
 * The theme prints "Sorry, no results were found" whenever WordPress itself
 * matched nothing, and it decides that on the server, before this widget has
 * run. So a search for "flannel fest", a real event with a real card rendered
 * directly above, still ended with the site telling the person it had found
 * nothing. That is the one message a search page must never get wrong.
 *
 * The apology goes back the moment we have nothing either (at any campus:
 * the chips' own line covers a campus with nothing): two sources finding
 * nothing between them is exactly what it says.
 *
 * Hiding the block alone would leave its container's 50px bottom margin
 * behind, so when the apology is all WordPress put there, the container goes
 * with it.
 */
function setThemeApology(config: WidgetConfig, visible: boolean) {
  const results = document.getElementById(config.resultsId || DEFAULT_RESULTS_ID);
  const apology = results?.querySelector<HTMLElement>('.no-results');
  if (!results || !apology) return;
  const target = results.childElementCount === 1 ? results : apology;
  // '' rather than 'block': the theme's own stylesheet decides how it looks.
  target.style.display = visible ? '' : 'none';
  // The apology carries its own search form; ours is in the title band.
  const form = apology.querySelector<HTMLElement>('form');
  if (form)
    form.style.display = document.getElementById(config.heroId || DEFAULT_HERO_ID) ? 'none' : '';
}

/**
 * Salient lays the "no results" page out in its narrow reading column
 * (`.main-content` capped at 1000px) while a page with results gets the
 * wide one (1600px). Our cards are the results now, so when WordPress found
 * nothing and we did, the column is widened to what the results page uses;
 * the cap goes back when we have nothing either.
 */
function matchResultsWidth(container: HTMLElement, wide: boolean) {
  if (!document.body.classList.contains('search-no-results')) return;
  const column = container.closest<HTMLElement>('.main-content');
  if (!column) return;
  if (wide) column.style.setProperty('max-width', '1600px', 'important');
  else column.style.removeProperty('max-width');
}

function mount(config: WidgetConfig, container: HTMLElement, query: string): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  const assetBaseUrl = (config.assetBaseUrl || DEFAULT_ASSET_BASE).replace(/\/$/, '');
  setSeeJesusAssetBase(assetBaseUrl);
  const hero = findHero(config);
  const widget: ShadowWidget = new ShadowWidget({
    assetBaseUrl,
    containerIds: [container.id],
    overlayHostId: 'site-search-overlay',
    fontId: 'site-search-widget-fonts',
    scrollLockFlag: '__SITE_SEARCH_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Site Search Widget',
    render: (el) => (
      <SiteSearch
        apiBaseUrl={apiBaseUrl}
        query={query}
        searchPath={window.location.pathname || '/'}
        queryParam={config.queryParam || 's'}
        toolbarContainer={hero ? widget.attachStyled(hero.el) : null}
        showCount={hero !== null && !hero.themeCounted}
        eventDetailsUrl={config.eventDetailsUrl ?? attr(el.dataset.eventDetailsUrl)}
        groupDetailsUrl={config.groupDetailsUrl ?? attr(el.dataset.groupDetailsUrl)}
        serveDetailsUrl={config.serveDetailsUrl ?? attr(el.dataset.serveDetailsUrl)}
        pageSize={config.pageSize}
        onResult={(counts) => {
          const found = Boolean(counts && counts.all > 0);
          annotateCount(config, counts);
          setThemeApology(config, !found);
          matchResultsWidth(container, found);
        }}
      />
    ),
  });
  widget.init();
  return widget;
}

// Auto-initialize
if (typeof window !== 'undefined') {
  // API base is the centralized api.woodsidebible.org host, baked at build
  // time from CHURCH_HUB_API_URL. window.SITE_SEARCH_WIDGET_CONFIG can
  // override for local testing.
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.SITE_SEARCH_WIDGET_CONFIG || {}),
  };
  window.SITE_SEARCH_WIDGET_CONFIG = config;

  const state = { query: null as string | null, widget: null as ShadowWidget | null, refresh };

  function refresh() {
    const query = currentQuery(config.queryParam || 's');
    if (!query || !isFirstPage()) return;
    if (state.widget && state.query === query) return;
    const container = findContainer(config);
    if (!container) return;
    // A new query (Search & Filter ajax): start over in the same container.
    if (state.widget) {
      state.widget.destroy();
      container.shadowRoot?.replaceChildren();
    }
    state.query = query;
    state.widget = mount(config, container, query);
  }

  // Skip if already initialized (e.g. script loaded twice via bfcache)
  if (!window.SiteSearchWidget) {
    window.SiteSearchWidget = state;
    const boot = () => {
      refresh();
      // Search & Filter Pro announces ajax results through jQuery only.
      try {
        window.jQuery?.(document).on('sf:ajaxfinish', () => setTimeout(refresh, 0));
      } catch {
        /* no jQuery on the page */
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
